import { RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { Button } from '~/components/ui/button';
import { useRetryFailedDeliveryRun } from '~/features/deliveries/hooks/use-delivery-controls';
import { useDeliveryRunDetail } from '~/features/deliveries/hooks/use-delivery-run-detail';
import { deriveDeliveryActions } from '~/features/deliveries/utils/derive-delivery-actions';
import { usePermission } from '~/hooks/usePermission';

interface Props {
  /** Integrations' delivery run id — `progress.delivery.externalJobRef` (the `pipeline_run_id`
   *  integrations returned when it accepted the push). */
  deliveryRunId: string;
}

/**
 * "Retry failed" for a catalog run's delivery, outside the Deliveries console. Shown only when
 * the delivery run itself offers `RETRY_FAILED` (server-side lifecycle + retry budget) AND the
 * user holds `deliveries:create` — the permission integrations checks on the endpoint — so the
 * button never leads to a 403/409. On success it stays put (the console may be out of reach):
 * the shared hook toasts the retried / skipped counts and invalidates the deliveries cache so
 * this panel re-reads.
 */
export function DeliveryRetryFailedButton({ deliveryRunId }: Props) {
  const { t } = useTranslation('deliveries');
  const canCreate = usePermission('deliveries:create');
  const runQuery = useDeliveryRunDetail(deliveryRunId, canCreate);
  const retry = useRetryFailedDeliveryRun(deliveryRunId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const run = runQuery.data;
  if (!canCreate || !run || !deriveDeliveryActions(run).canRetryFailed) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 cursor-pointer text-xs"
        isLoading={retry.isPending}
        onClick={() => {
          setConfirmOpen(true);
        }}
      >
        <RotateCcw aria-hidden="true" />
        {t('controls.retry')}
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('controls.confirm.retryTitle')}
        description={t('controls.confirm.retryBody')}
        confirmLabel={t('controls.confirm.retryConfirm')}
        cancelLabel={t('controls.confirm.notNow')}
        isPending={retry.isPending}
        onConfirm={() => {
          retry.mutate(undefined, {
            onSuccess: () => {
              setConfirmOpen(false);
            },
          });
        }}
      />
    </>
  );
}
